import React, { useContext, useState, useEffect, useMemo } from "react";
import "./Header.css";
import {
  ILessonManagementContext,
  LessonManagementContext,
  ILessonPageActionsContext,
  LessonPageActionsContext,
  AssetContext,
} from "../routes/builderContexts";
import GlobalAudioManager from "../classes/GlobalAudioManager/GlobalAudioManager";
import LessonPreview from "./LessonPreview";
import HeaderAnnouncer from "./HeaderAnnouncer/HeaderAnnouncer";
import { useLessonData } from "../contexts/LessonDataProvider";
import { connect } from "react-redux";
import ILessonPageDto from "../models/ILessonPageDto";
import { useMiscUI } from "../contexts/MiscUI/MiscUIProvider";
import { ReactComponent as AddPageIcon } from "../assets/icons/HeaderIcons/add-page-icon.svg";
import { ReactComponent as RemovePageIcon } from "../assets/icons/Common/remove-page-icon.svg";
import { ReactComponent as CopyPageIcon } from "../assets/icons/HeaderIcons/copy-page-icon.svg";
import { ReactComponent as SavePageIcon } from "../assets/icons/HeaderIcons/save-page-icon.svg";
import { ReactComponent as SettingsLessonIcon } from "../assets/icons/HeaderIcons/settings-lesson-icon.svg";
import { ReactComponent as DraftLessonIcon } from "../assets/icons/HeaderIcons/draft_document_white.svg";

import { ReactComponent as SavingSpinner } from "../assets/icons/HeaderIcons/loading-sequence-airplane-icon.svg";
import { ReactComponent as AssetManagerIcon } from "../assets/icons/HeaderIcons/asset-manager-with-background-icon.svg";
import { ReactComponent as InventLogo } from "../assets/Logos/invent-logo-white.svg";
import CopyPageFromLesson from "./Modals/CopyPageFromLesson/CopyPageFromLesson";
import useSettingsToggle, { Settings, settingType } from "../hooks/useSettingsToggle";
import { AbstractTooltip as Tooltip } from "./ToolTip/ToolTip";
import { ObjectActionsType, useObjectsDispatch, useObjectsState } from "../contexts/ObjectsProvider";
import {
  useInteracitvityHotspotDispatch,
  InteractivityHotspotActionTypes,
} from "../contexts/InteractivityHotspotProvider";
import NotificationsPanel from "./NotificationsPanel";
import { useConfirmation } from "../contexts/Confirmation";
import genericRepositoryService from "../services/genericRepositoryService";
import ExportStatusModal from "./Modals/ExportStatusModal/ExportStatusModal";
import { DevOnly } from "../hooks/useEnvironment";
import { canUsePage } from "../utils/pageRestrictions";

interface IButtonsContext {
  dirt: number;
}

export const ButtonsContext: React.Context<IButtonsContext> = React.createContext<IButtonsContext>(
  {} as IButtonsContext,
);

const AddPageButton: React.FC = () => {
  const lessonContext: ILessonManagementContext = useContext<ILessonManagementContext>(LessonManagementContext);

  return (
    <div className="icons-wrapper" data-tooltip-id="addPage">
      <div className="header-icon">
        <AddPageIcon onClick={() => lessonContext.setSelectorModalInfo("Add Page")} name="Add Page" />
      </div>
      <p>Add Page</p>
      <Tooltip id="addPage" />
    </div>
  );
};

const SaveTimer = ({ reset, unsavedChanges }: { reset: boolean; unsavedChanges: boolean }) => {
  const [counter, setCounter] = useState<number>(0);
  useEffect(() => {
    const runner = setInterval(() => {
      setCounter((count: number) => {
        count++;
        return count;
      });
    }, 1000);

    return () => {
      setCounter(0);
      clearInterval(runner);
    };
  }, [reset, unsavedChanges]);
  let output = "Saved";
  if (counter <= 60 && unsavedChanges) output = "Less than one min";
  else if (counter >= 3600 && unsavedChanges) output = "Greater than 60 min";
  else if (counter > 10 && unsavedChanges) output = `${Math.round(counter / 60)} min`;

  return <p>{output}</p>;
};

const SaveButton: React.FC<{ isReadOnly: boolean }> = ({ isReadOnly }) => {
  const { confirm: confirmSave } = useConfirmation();
  const lessonManagementContext: ILessonManagementContext =
    useContext<ILessonManagementContext>(LessonManagementContext);
  const objectsDispatch = useObjectsDispatch();
  const lessonPageActions: ILessonPageActionsContext = useContext<ILessonPageActionsContext>(LessonPageActionsContext);
  const interacitvityHotspotDispatch = useInteracitvityHotspotDispatch();
  const { needSave } = useObjectsState();
  const { dirt, setDirt } = lessonPageActions;
  const dirtyPages = lessonManagementContext.lessonPages.filter((page: ILessonPageDto) => {
    return page.lessonPageIsDirty || page.areAssetsDirty || page.pageManifestIsDirty || page.isClientSideNewPage;
  });
  if (dirtyPages.length > 0) {
    dirt !== 5 && setDirt(5);
  } else {
    dirt !== 0 && setDirt(0);
  }

  const saveClick = () => {
    GlobalAudioManager.stop();
    lessonManagementContext.saveLesson(lessonManagementContext.lessonPages);
    objectsDispatch({ type: ObjectActionsType.CLEAR_NEED_SAVE });
    interacitvityHotspotDispatch({ type: InteractivityHotspotActionTypes.CLEAR_NEED_SAVE });
  };

  function pendingChangesCheck(cb: () => void) {
    const hasPendingChanges = lessonManagementContext.lessonPages.some(
      (page: ILessonPageDto) => page.pageManifest.replacedAssets?.length > 0,
    );

    if (!hasPendingChanges) {
      cb();

      return;
    }

    confirmSave({
      title: "Pending Review",
      message: "Some pages in the lesson need review, do you wish to continue?",
    }).then((result) => {
      if (result) {
        cb();
      }
    });
  }

  const isDirty = needSave || lessonPageActions.dirt !== 0;

  return (
    <div className="icons-wrapper" data-tooltip-id="saveLesson">
      <Tooltip id="saveLesson" />
      <div className="header-icon">
        {lessonManagementContext.isSaved ? (
          <SavePageIcon
            onClick={() => {
              pendingChangesCheck(saveClick);
            }}
          />
        ) : (
          <div className="spinner-container">
            <SavingSpinner className="saving-spinner" />
          </div>
        )}
      </div>
      <SaveTimer reset={lessonManagementContext.isSaved} unsavedChanges={isDirty} />
    </div>
  );
};

const RemovePageButton = () => {
  const lessonManagementContext: ILessonManagementContext =
    useContext<ILessonManagementContext>(LessonManagementContext);

  const disabledButton = !lessonManagementContext.pageIndexIsSafeToDelete();
  const tooltipId = "Remove Page";
  const headerIconClassName = disabledButton ? "pointer-events-none" : "";
  const className = disabledButton ? "disabled-header-button" : "header-button";

  return (
    <div className="icons-wrapper" data-tooltip-id={tooltipId}>
      <Tooltip id={tooltipId} />
      <div className={`header-icon ${headerIconClassName}`}>
        <RemovePageIcon className={className} onClick={lessonManagementContext.deletePage} />
      </div>
      <p>Remove Page</p>
    </div>
  );
};

interface IDesignerHeader {
  lessonName?: string;
  lessonVersionId?: number;
  lessonVersionDisplayableId: number;
  lmsName?: string;
  setLessonSettingsController: (options: { isOpen: boolean }) => void;
  setCoursewareDeveloperToolsController?: any;
  setAssetTypeId(id: number): void;
  isCpatUser?: boolean;
  lmsKey: number;
  userKey: number;
  changePageOrder?: (newOrderLessonPages: ILessonPageDto[], pageToGoTo?: number) => void;
}

const CopyPage: React.FC<{ isReadOnly: boolean }> = ({ isReadOnly }) => {
  const lessonManagementContext: ILessonManagementContext =
    useContext<ILessonManagementContext>(LessonManagementContext);
  const { copyPage, currentlyDisplayedPageIndex, lessonPages } = lessonManagementContext;
  const [miscUIState, miscUIDispatch] = useMiscUI();
  const permissions = useLessonData({ permissions: true });
  const page = lessonPages[currentlyDisplayedPageIndex];

  const canCopy =
    currentlyDisplayedPageIndex >= 4 &&
    currentlyDisplayedPageIndex <= lessonPages.length - 4 &&
    permissions.canEdit &&
    permissions.canEdit(page.pagePlayerType) &&
    permissions.canCreate(page.pagePlayerType) &&
    canUsePage(page);

  const disabled = useMemo(() => {
    if (isReadOnly) {
      return true;
    }

    return !canCopy;
  }, [canCopy, isReadOnly]);

  const headerIconClassName = disabled ? "pointer-events-none" : "";
  const className = disabled ? "disabled-header-button" : "header-button";

  const handleOp = async () => {
    if (canCopy) {
      // let updatedLessonPages = await saveLesson(lessonPages);
      //the last paramater will overwrite the lesson pages used before this block is called so we may have the frsh updated pages (updatedLessonPages) that the save lesson modified
      miscUIDispatch({ type: "TOGGLE_COPYING" });
      // addNewPageAndDisplay((currentlyDisplayedPageIndex + 1), page.pagePlayerType, page.name, page.pageVersionId, updatedLessonPages )
      await copyPage(currentlyDisplayedPageIndex + 1, lessonPages as ILessonPageDto[]); // await does have effect here
      miscUIDispatch({ type: "TOGGLE_COPYING" });
    } else {
      console.warn("cant copy the page");
    }
  };
  return (
    <div className="icons-wrapper" data-tooltip-id="Copy Page">
      <Tooltip id="Copy Page" />
      <div className={`header-icon ${headerIconClassName}`}>
        {miscUIState.isCopying ? (
          <div className="spinner-container">
            <SavingSpinner className="saving-spinner" />
          </div>
        ) : (
          <CopyPageIcon
            className={className}
            onClick={() => {
              handleOp();
            }}
            name="CopyPage"
          />
        )}
      </div>
      <p>Copy Page</p>
    </div>
  );
};

const renderSettings: Settings = {
  copyPagesFromLesson: settingType.cpatonly,
};

const DesignerHeader: React.FC<IDesignerHeader> = (props: IDesignerHeader) => {
  const lessonManagementContext: ILessonManagementContext =
    useContext<ILessonManagementContext>(LessonManagementContext);
  const lessonPageActions = useContext<ILessonPageActionsContext>(LessonPageActionsContext);
  const [featuresToRender] = useSettingsToggle(renderSettings);
  const { allPageAssetsUseActiveVersion } = useContext(AssetContext);
  const { copyPagesFromLesson } = featuresToRender;
  const [blobLink] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const isMicroLesson = lessonManagementContext.currentAggregationOrder !== null;
  const { confirm } = useConfirmation();
  const { lessonMetaData } = useContext(AssetContext);
  const lessonDesignerVersion = lessonMetaData?.lessonDesignerVersion;

  const handleDraftLesson = async () => {
    // Ask for confirmation first
    const shouldDraft = await confirm({
      title: "Draft Lesson",
      message: "Do you want to draft this lesson?",
      type: "confirmation",
    });

    if (!shouldDraft) {
      return;
    }

    const lmsDomain = window.location.hostname?.split(`.`)?.at(0) ?? "cpattest";

    // Open the export status modal immediately
    setIsExportModalOpen(true);

    try {
      const response = await genericRepositoryService.draftLesson({
        LessonVersionId: props.lessonVersionId as number,
        LmsKey: props.lmsKey,
        UserKey: props.userKey,
        IsBeta: true,
        LmsDomain: lmsDomain,
      });

      // Check if the response has a job ID (indicating the draft was queued successfully)
      if (!response.isSuccess || !response?.data?.jobId) {
        // Handle error - close modal and show error
        setIsExportModalOpen(false);
        console.error("Draft lesson failed:", response.errorMessage);
        alert(`Failed to draft lesson: ${response.errorMessage || "Unknown error"}`);
      }
      // If successful, the modal will continue polling for status updates
    } catch (error) {
      // Close modal on error
      setIsExportModalOpen(false);
      console.error("Error drafting lesson:", error);
      alert("An error occurred while drafting the lesson");
    }
  };

  const DraftLessonButton: React.FC = () => {
    return props.isCpatUser ? (
      <div className="icons-wrapper" data-tooltip-id="Draft">
        <div className="header-icon">
          <DraftLessonIcon
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={() => {
              handleDraftLesson();
            }}
          />
        </div>
        <p>Draft</p>
        <Tooltip id="Draft" />
      </div>
    ) : null;
  };

  const LessonSettingsButton: React.FC = () => {
    return (
      <div className="icons-wrapper" data-tooltip-id="Settings">
        <div className={`header-icon`}>
          <SettingsLessonIcon
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={() => {
              props.setAssetTypeId(-1);
              props.setLessonSettingsController({ isOpen: true });
            }}
          />
        </div>
        <p>Settings</p>
        <Tooltip id="Settings" />
      </div>
    );
  };

  const showBlobDownload = () => {
    if (blobLink && blobLink.length > 0) {
      return (
        <div id="downloadSCOLinkContainer">
          <a id="downloadSCOLink" href={blobLink}>
            {/* {FilePart(blobLink)} */}
            Download Export
          </a>
        </div>
      );
    }
  };

  const AssetManagerButton = () => {
    return (
      <div className="icons-wrapper" data-tooltip-id="Asset Manager">
        <Tooltip id="Asset Manager" />
        <button
          className="header-icon assetManager-icon"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onClick={() => {
            lessonManagementContext.setAssetManagerController({
              isOpen: true,
              mode: "Standalone",
            });
            props.setAssetTypeId(-1);
          }}
        >
          <AssetManagerIcon />
          <p>Asset Library Manager</p>
        </button>
        <p style={{ color: "var(--icon-header-color)", userSelect: "none" }}>AML</p>
      </div>
    );
  };

  function DeveloperToolsButton({ isCpatUser, isReadOnly }: { isCpatUser: boolean; isReadOnly: boolean }) {
    return isCpatUser || copyPagesFromLesson ? (
      <CopyPageFromLesson
        isReadOnly={isReadOnly}
        CurrentLessonVersionId={props.lessonVersionId as number}
        ChangePageOrder={props.changePageOrder}
      />
    ) : null;
  }

  return (
    <div>
      <div className="designer-header">
        <div className="headerLV">
          <InventLogo height="100%" width="100%" />
        </div>
        <div className="branding">{props && props.lessonName ? `${props.lessonName}` : ""}</div>

        {showBlobDownload()}

        <div className="headerRV-container">
          <div className="headerRV">
            <p>
              <strong>LMS:</strong>
              <span>({props.lessonVersionId})</span>
              {props.lmsName}
              <p />
              <strong> Invent Version:</strong>
              <span>{lessonDesignerVersion ?? "v2"}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="iconheader">
        <div className="buttons-left">
          <AddPageButton />
          <CopyPage isReadOnly={isMicroLesson} />
          <RemovePageButton />
        </div>
        <div className="developer-tools-portal">
          <DeveloperToolsButton isReadOnly={isMicroLesson} isCpatUser={!!props?.isCpatUser} />
        </div>
        <div id="lesson-version-display">
          <HeaderAnnouncer handleApiError={lessonManagementContext.handleApiError}>
            {lessonManagementContext.errorMessage}
          </HeaderAnnouncer>
        </div>
        <div className="buttons-right">
          <NotificationsPanel
            newNotifications={!allPageAssetsUseActiveVersion}
            lessonVersionId={props.lessonVersionId}
          />
          <AssetManagerButton />
          <LessonPreview lessonVersionId={props.lessonVersionId} />
          <DevOnly>
            <DraftLessonButton />
          </DevOnly>
          <SaveButton isReadOnly={isMicroLesson} />
          <LessonSettingsButton />
          {/* <DraftLessonButton /> */}
        </div>
      </div>

      <ExportStatusModal
        isOpen={isExportModalOpen}
        lessonVersionId={props.lessonVersionId as number}
        onClose={() => {
          setIsExportModalOpen(false);
        }}
      />
    </div>
  );
};

const mapProps = (state: any) => ({
  isCpatUser: state.authorizedState.isCpatUser,
  lmsKey: state.authorizedState.lmsKey,
  userKey: state.authorizedState.userKey,
});

export default connect(mapProps, null)(DesignerHeader);
